import axios from 'axios';
import { IUser } from '../../user';
import { ApiResponse, GetReportResponse, zps_ms_ea_war } from '../../zps';
import { handleDownload } from '../../../app/utils/script';

export type IPCTickets = {
  action?: number;
  cause?: number;
  fault?: number;
  problem?: number;
  seqn?: number;
};

export interface HeldeskClassificationItems {
  id: number;
  name: string;
}

export interface HeldeskClassificationResponse extends ApiResponse {
  actions: HeldeskClassificationItems[];
  causes: HeldeskClassificationItems[];
  faults: HeldeskClassificationItems[];
  problems: HeldeskClassificationItems[];
}
export interface HeldeskTicketsRequests {
  agent?: { entid: number; name: string };
  commChannel?: number;
  contact?: string;
  eid?: number;
  entid?: number;
  entityName?: string;
  intType?: number;
  issueType?: number;
  openDT?: string;
  pc?: IPCTickets[];
  sn?: string;
  status?: number;
  statusDT?: string;
  ticketID?: number;
  time?: number;
  urgency?: number;
}

export type ticketInfoLines = {
  classification?: number;
  comments?: string;
  description?: string;
  impact?: number;
  line?: number;
  origin?: string;
  soluClassif?: boolean;
  status?: number;
  statusDT?: string;
  ticketID?: number;
  time?: number;
  urgency?: number;
  user: IUser;
};

export type ticketInfoPc = {
  action: number;
  cause: number;
  fault: number;
  problem: number;
};

export interface HeldeskTicketInfoRequests {
  agent?: { entid: number; name: string };
  commChannel?: number;
  contact?: string;
  eid?: number;
  entid?: number;
  entityName?: string;
  intType?: number;
  issueType?: number;
  lines?: ticketInfoLines[];
  openDT?: string;
  pc?: ticketInfoPc[];
  product: string;
  sn?: string;
  status?: number;
  statusDT?: string;
  system?: string;
  ticketID: number;
  time?: number;
  urgency?: number;
  description?: string;
  comments?: string;
}

export interface HeldeskTicketInfoUpdateRequests {
  commChannel?: number;
  contact?: string;
  eid: number;
  entid: number;
  intType?: number;
  issueType?: number;
  openDT?: string;
  pc?: ticketInfoPc[];
  system?: null;
  ticketID?: number;
  description?: string;
  comments?: string;
}

export interface HeldeskTickeLineRequests {
  classification: number;
  comments: string;
  description: string;
  impact: number;
  line: number;
  origin: string;
  soluClassif: string;
  status: number;
  statusDT: string;
  ticketID: number;
  time: number;
  urgency: number;
  user: IUser;
}

export interface IHelpdeskTicketsResponse extends ApiResponse {
  tickets: HeldeskTicketsRequests[];
  totalRows: number;
  type: string;
}

export interface IHelpdeskTicketInfoResponse extends ApiResponse {
  tickets: HeldeskTicketInfoRequests[];
  totalRows: number;
  type: string;
}

export const fetchHelpdeskTickets = (
  controller: AbortController,
  offset: number,
  limit: number,
  totalRows: boolean,
  ticketID?: number,
  eid?: number,
  entid?: number,
  closed?: string,
  bDT?: string | null,
  eDT?: string
): Promise<IHelpdeskTicketsResponse> => {
  const url =
    `${zps_ms_ea_war}/ticket?offset=${offset}&limit=${limit}&totalRows=${totalRows}` +
    (ticketID ? `&ticketID=${ticketID}` : '') +
    (eid ? `&eid=${eid}` : '') +
    (entid ? `&entid=${entid}` : '') +
    (closed ? `&closed=${closed}` : '') +
    (bDT ? `&bDT=${bDT}` : '') +
    (eDT ? `&eDT=${eDT}` : '');

  return axios.get<IHelpdeskTicketsResponse>(url, { signal: controller.signal }).then(res => res.data);
};

export const fetchHelpdeskTicketInfo = (
  ticketID: number,
  controller: AbortController
): Promise<IHelpdeskTicketInfoResponse> => {
  return axios
    .get<IHelpdeskTicketInfoResponse>(`${zps_ms_ea_war}/ticket/${ticketID}`, { signal: controller.signal })
    .then(res => res.data);
};

export const UpdateHelpdeskTicketInfo = (
  request: HeldeskTicketInfoUpdateRequests,
  controller: AbortController
): Promise<IHelpdeskTicketInfoResponse> => {
  return axios
    .put<IHelpdeskTicketInfoResponse>(`${zps_ms_ea_war}/ticket/`, request, { signal: controller.signal })
    .then(res => res.data);
};

export const fetchTicketClassifications = (controller: AbortController): Promise<HeldeskClassificationResponse> => {
  return axios
    .get<HeldeskClassificationResponse>(`${zps_ms_ea_war}/ticket/classification`, { signal: controller.signal })
    .then(res => res.data);
};

export const addTicket = (
  request: HeldeskTicketInfoRequests,
  controller: AbortController
): Promise<IHelpdeskTicketInfoResponse> => {
  return axios
    .post<IHelpdeskTicketInfoResponse>(`${zps_ms_ea_war}/ticket/`, request, { signal: controller.signal })
    .then(res => res.data);
};

export const updateTicket = (
  request: HeldeskTicketInfoUpdateRequests,
  controller: AbortController
): Promise<IHelpdeskTicketInfoResponse> => {
  return axios
    .put<IHelpdeskTicketInfoResponse>(`${zps_ms_ea_war}/ticket/`, request, { signal: controller.signal })
    .then(res => res.data);
};

export const addTicketLine = (
  request: HeldeskTickeLineRequests,
  controller: AbortController
): Promise<IHelpdeskTicketInfoResponse> => {
  return axios
    .post<IHelpdeskTicketInfoResponse>(`${zps_ms_ea_war}/ticket/line`, request, { signal: controller.signal })
    .then(res => res.data);
};

export const updateTicketLine = (
  request: HeldeskTickeLineRequests,
  controller: AbortController
): Promise<IHelpdeskTicketInfoResponse> => {
  return axios
    .put<IHelpdeskTicketInfoResponse>(`${zps_ms_ea_war}/ticket/line`, request, { signal: controller.signal })
    .then(res => res.data);
};

export const fetchTicketsXLS = (
  controller: AbortController,
  eDT: string,
  closed: string,
  ticketID?: number,
  eid?: number,
  entid?: number,
  bDT?: string
): Promise<ApiResponse> => {
  return axios
    .get<GetReportResponse>(
      `${zps_ms_ea_war}/ticket/report?format=xls&eDT=${eDT}&closed=${closed}` +
        `${ticketID ? `&ticketID=${ticketID}` : ''}` +
        `${eid ? `&eid=${eid}` : ''}` +
        `${entid ? `&entid=${entid}` : ''}` +
        `${bDT ? `&bDT=${bDT}` : ''}`,
      { signal: controller.signal }
    )
    .then(res => {
      handleDownload(res.data.report, res.data.name, res.data.mime);
      return res.data;
    });
};
